import React, { useState, useEffect } from 'react';

const Performance = ({ userRole, userId }) => {
  const [activeTab, setActiveTab] = useState('tests');
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [testSubmitted, setTestSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [results, setResults] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
    loadTests();
  }, []);

  useEffect(() => {
    let interval;
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            if (selectedTest && !testSubmitted) {
              handleSubmit();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeRemaining, selectedTest]);

  const loadTests = () => {
    const savedTests = localStorage.getItem('performanceTests');
    if (savedTests) {
      setTests(JSON.parse(savedTests));
    } else {
      // Initialize with 30+ questions per test
      const sampleTests = [
        {
          id: 1,
          title: 'Software Engineering Fundamentals',
          designation: ['employee', 'intern', 'senior'],
          duration: 60,
          totalQuestions: 30,
          questions: generateSoftwareEngineeringQuestions()
        },
        {
          id: 2,
          title: 'Database Management System',
          designation: ['employee', 'intern', 'senior'],
          duration: 60,
          totalQuestions: 30,
          questions: generateDBMSQuestions()
        },
        {
          id: 3,
          title: 'Leadership & Management Skills',
          designation: ['manager', 'hr', 'admin', 'superadmin'],
          duration: 60,
          totalQuestions: 30,
          questions: generateLeadershipQuestions()
        },
        {
          id: 4,
          title: 'HR Policies & Compliance',
          designation: ['hr', 'admin', 'superadmin'],
          duration: 60,
          totalQuestions: 30,
          questions: generateHRQuestions()
        },
        {
          id: 5,
          title: 'Data Structures & Algorithms',
          designation: ['employee', 'intern', 'senior'],
          duration: 75,
          totalQuestions: 30,
          questions: generateDSAQuestions()
        },
        {
          id: 6,
          title: 'Project Management Professional',
          designation: ['manager', 'admin', 'superadmin'],
          duration: 60,
          totalQuestions: 30,
          questions: generatePMPQuestions()
        }
      ];
      setTests(sampleTests);
      localStorage.setItem('performanceTests', JSON.stringify(sampleTests));
    }
  };

  // Generate 30 Software Engineering Questions
  const generateSoftwareEngineeringQuestions = () => {
    const questions = [];
    const seQuestions = [
  { text: 'What is software engineering?', options: ['Writing code only', 'Managing hardware', 'Systematic approach to software development', 'Database management'], correct: 2 },
  { text: 'Which of the following is a phase in the waterfall model?', options: ['Design', 'Coding only', 'Testing only', 'Deployment only'], correct: 0 },
  { text: 'What does SDLC stand for?', options: ['Software Design Life Cycle', 'System Development Life Cycle', 'Software Development Life Cycle', 'System Design Life Cycle'], correct: 2 },
  { text: 'What is a functional requirement?', options: ['Speed of system', 'Security of system', 'What the system should do', 'How the system performs'], correct: 2 },
  { text: 'What is a non-functional requirement?', options: ['User login feature', 'Payment processing', 'System response time', 'Data entry form'], correct: 2 },
  { text: 'What is an SRS document?', options: ['Software Requirements Specification', 'System Requirement Standard', 'Software Review System', 'System Review Specification'], correct: 0 },
  { text: 'Who are stakeholders in a software project?', options: ['Only developers', 'Only testers', 'Only managers', 'Anyone with interest in the project'], correct: 3 },
  { text: 'Which software model is most rigid and sequential?', options: ['Agile', 'Waterfall', 'Spiral', 'Scrum'], correct: 1 },
  { text: 'Which software model is iterative and incremental?', options: ['Waterfall', 'V-Model', 'Agile', 'Big Bang'], correct: 2 },
  { text: 'What is verification in software testing?', options: ['Are we building the right product?', 'Are we building the product right?', 'User acceptance testing', 'Performance testing'], correct: 1 },
  { text: 'What is validation in software testing?', options: ['Are we building the product right?', 'Code review', 'Are we building the right product?', 'Unit testing'], correct: 2 },
  { text: 'What is technical debt?', options: ['Money owed to vendors', 'Cost of future rework due to shortcuts', 'Hardware cost', 'Licensing fees'], correct: 1 },
  { text: 'What is version control used for?', options: ['Managing code changes over time', 'Compiling code', 'Deploying code', 'Writing code'], correct: 0 },
  { text: 'What is a use case?', options: ['A test case', 'A scenario describing system interaction', 'A code function', 'A database table'], correct: 1 },
  { text: 'What is software configuration management?', options: ['Managing software versions and changes', 'Writing code', 'Testing software', 'Designing software'], correct: 0 },
  { text: 'What is black-box testing?', options: ['Testing internal structure', 'Testing without knowing internal code', 'Testing only one function', 'Testing database'], correct: 1 },
  { text: 'What is white-box testing?', options: ['Testing user interface', 'Testing without code knowledge', 'Testing internal logic and code', 'Testing requirements only'], correct: 2 },
  { text: 'What is unit testing?', options: ['Testing entire system', 'Testing individual components or functions', 'Testing user acceptance', 'Testing performance'], correct: 1 },
  { text: 'What is integration testing?', options: ['Testing one function', 'Testing how modules work together', 'Testing user interface', 'Testing security'], correct: 1 },
  { text: 'What is a software design pattern?', options: ['A bug', 'A reusable solution to common problems', 'A programming language', 'A database schema'], correct: 1 },
  { text: 'What is the Single Responsibility Principle?', options: ['A class should have one reason to change', 'A class should do everything', 'Functions should be long', 'Code should be complex'], correct: 0 },
  { text: 'What is coupling in software design?', options: ['Dependency between modules', 'Lack of features', 'Code duplication', 'Slow performance'], correct: 0 },
  { text: 'What is cohesion in software design?', options: ['How unrelated modules are', 'How focused a single module is', 'How many bugs exist', 'How fast code runs'], correct: 1 },
  { text: 'What is continuous integration?', options: ['Deploying once a year', 'Frequently merging code changes into main branch', 'Writing documentation', 'Manual testing only'], correct: 1 },
  { text: 'What is a sprint in Scrum?', options: ['A race', 'A time-boxed development iteration', 'A meeting', 'A code review'], correct: 1 },
  { text: 'What is a product backlog?', options: ['List of completed tasks', 'List of bugs only', 'Prioritized list of features and requirements', 'Code repository'], correct: 2 },
  { text: 'What is risk management in software engineering?', options: ['Ignoring problems', 'Identifying and mitigating potential issues', 'Only coding', 'Only testing'], correct: 1 },
  { text: 'What is software maintainability?', options: ['How easy software can be modified', 'How fast software runs', 'How secure software is', 'How cheap software is'], correct: 0 },
  { text: 'What is the difference between verification and validation?', options: ['No difference', 'Verification checks product built right, validation checks right product', 'Verification is after deployment', 'Validation is before coding'], correct: 1 },
  { text: 'What is the Capability Maturity Model Integration (CMMI)?', options: ['A programming language', 'A process improvement framework', 'A testing tool', 'A database system'], correct: 1 }
];
    
    for (let i = 0; i < 30; i++) {
      const q = seQuestions[i % seQuestions.length];
      questions.push({
        id: i + 1,
        text: q.text,
        options: q.options,
        correct: q.correct
      });
    }
    return questions;
  };

  // Generate 30 DBMS Questions
  const generateDBMSQuestions = () => {
    const questions = [];
    const dbmsQuestions = [
  { text: 'What is a database?', options: ['Organized collection of data', 'Collection of files', 'Memory storage device', 'Network of computers'], correct: 0 },
  { text: 'What does DBMS stand for?', options: ['Database Management System', 'Data Backup Management System', 'Database Maintenance System', 'Data Binary Management System'], correct: 0 },
  { text: 'What is SQL used for?', options: ['Managing relational databases', 'Creating graphics', 'Building websites', 'Word processing'], correct: 0 },
  { text: 'What is a primary key?', options: ['Uniquely identifies each record', 'Links two tables', 'Sorts data', 'Indexes data'], correct: 0 },
  { text: 'What is a foreign key?', options: ['References primary key in another table', 'Unique identifier in same table', 'Index for fast search', 'Constraint for data type'], correct: 0 },
  { text: 'What is the purpose of normalization?', options: ['Reduce data redundancy and dependency', 'Increase data duplication', 'Make tables larger', 'Slow down queries'], correct: 0 },
  { text: 'What is First Normal Form (1NF)?', options: ['Each column has atomic values', 'No partial dependency', 'No transitive dependency', 'All keys are candidate keys'], correct: 0 },
  { text: 'What is Second Normal Form (2NF)?', options: ['No partial dependency on primary key', 'Atomic values only', 'No transitive dependency', 'Boyce-Codd Normal Form'], correct: 0 },
  { text: 'What is Third Normal Form (3NF)?', options: ['No transitive dependency', 'No partial dependency', 'Atomic values only', 'All attributes are prime'], correct: 0 },
  { text: 'What is a candidate key?', options: ['Minimal set of attributes that uniquely identifies a row', 'Key from another table', 'Key with null values', 'Duplicate key'], correct: 0 },
  { text: 'What does a JOIN operation do?', options: ['Combines rows from two or more tables', 'Deletes rows from a table', 'Creates a new table', 'Updates existing records'], correct: 0 },
  { text: 'What is an INNER JOIN?', options: ['Returns only matching records from both tables', 'Returns all records from left table', 'Returns all records from right table', 'Returns all records from both tables'], correct: 0 },
  { text: 'What is a LEFT JOIN?', options: ['Returns all records from left table and matched from right', 'Returns only matching records', 'Returns all records from right table', 'Returns no records if no match'], correct: 0 },
  { text: 'What is a RIGHT JOIN?', options: ['Returns all records from right table and matched from left', 'Returns only matching records', 'Returns all records from left table', 'Returns no records if no match'], correct: 0 },
  { text: 'What is a FULL OUTER JOIN?', options: ['Returns all records when there is a match in either table', 'Returns only matching records', 'Returns only left table records', 'Returns only right table records'], correct: 0 },
  { text: 'What is a view in SQL?', options: ['A virtual table based on query result', 'A physical table', 'An index', 'A stored procedure'], correct: 0 },
  { text: 'What is an index in a database?', options: ['Data structure for fast lookups', 'A type of table', 'A backup copy', 'A query log'], correct: 0 },
  { text: 'What is a database transaction?', options: ['A sequence of operations treated as one unit', 'A single SELECT query', 'A table creation', 'A backup operation'], correct: 0 },
  { text: 'What does ACID stand for?', options: ['Atomicity, Consistency, Isolation, Durability', 'Accuracy, Completeness, Isolation, Durability', 'Atomicity, Completeness, Integrity, Durability', 'Availability, Consistency, Integrity, Durability'], correct: 0 },
  { text: 'What is Atomicity in ACID?', options: ['Transaction completes fully or not at all', 'Database remains in valid state', 'Transactions execute independently', 'Committed changes persist'], correct: 0 },
  { text: 'What is Isolation in ACID?', options: ['Concurrent transactions do not interfere', 'Transaction completes fully', 'Database remains valid', 'Changes are permanent'], correct: 0 },
  { text: 'What is Durability in ACID?', options: ['Committed changes survive system failures', 'Transaction is atomic', 'Database is consistent', 'Transactions are isolated'], correct: 0 },
  { text: 'What is a stored procedure?', options: ['Precompiled SQL code stored in database', 'A simple SELECT query', 'A database index', 'A foreign key constraint'], correct: 0 },
  { text: 'What is a database trigger?', options: ['Automatically executes on certain events', 'A manually run query', 'An index type', 'A backup schedule'], correct: 0 },
  { text: 'What is denormalization?', options: ['Adding redundancy to improve read performance', 'Removing redundancy', 'Splitting tables', 'Creating foreign keys'], correct: 0 },
  { text: 'What is a deadlock in DBMS?', options: ['Two or more transactions waiting for each other', 'A fast transaction', 'A query error', 'A table lock'], correct: 0 },
  { text: 'What is NoSQL database?', options: ['Non-relational database for unstructured data', 'SQL database with no tables', 'Relational database', 'Object-oriented database'], correct: 0 },
  { text: 'What is sharding in databases?', options: ['Horizontal partitioning of data across servers', 'Vertical partitioning', 'Creating indexes', 'Backing up data'], correct: 0 },
  { text: 'What is the difference between DELETE and TRUNCATE?', options: ['DELETE is DML (can rollback), TRUNCATE is DDL', 'DELETE is faster', 'TRUNCATE can use WHERE', 'No difference'], correct: 0 },
  { text: 'What is a composite primary key?', options: ['Primary key consisting of two or more columns', 'Key that allows nulls', 'Key from another table', 'Index key'], correct: 0 }
];
    
    for (let i = 0; i < 30; i++) {
      const q = dbmsQuestions[i % dbmsQuestions.length];
      questions.push({
        id: i + 1,
        text: q.text,
        options: q.options,
        correct: q.correct
      });
    }
    return questions;
  };

  // Generate 30 Leadership Questions
  const generateLeadershipQuestions = () => {
    const questions = [];
    const leadershipQuestions = [
      { text: 'What is leadership?', options: ['Influencing others', 'Managing tasks', 'Controlling resources', 'Planning work'], correct: 0 },
      { text: 'What is the difference between leader and manager?', options: ['Leader inspires, manager controls', 'Leader controls, manager inspires', 'Both are same', 'None of the above'], correct: 0 },
      { text: 'What is transformational leadership?', options: ['Inspiring change', 'Maintaining status quo', 'Controlling people', 'Micromanaging'], correct: 0 },
      { text: 'What is transactional leadership?', options: ['Rewards and punishments', 'Inspiring vision', 'Participative decision', 'Laissez-faire'], correct: 0 },
      { text: 'What is servant leadership?', options: ['Serving others first', 'Commanding others', 'Controlling others', 'Dictating others'], correct: 0 },
      { text: 'What is democratic leadership?', options: ['Participative decisions', 'Autocratic decisions', 'No decisions', 'Random decisions'], correct: 0 },
      { text: 'What is autocratic leadership?', options: ['Centralized control', 'Participative', 'Delegative', 'Collaborative'], correct: 0 },
      { text: 'What is laissez-faire leadership?', options: ['Hands-off approach', 'Hands-on approach', 'Controlling approach', 'Directive approach'], correct: 0 },
      { text: 'What is emotional intelligence?', options: ['Understanding emotions', 'Technical skills', 'Management skills', 'Communication skills'], correct: 0 },
      { text: 'What are the components of EQ?', options: ['Self-awareness, empathy, social skills', 'IQ, technical skills, management', 'Planning, organizing, controlling', 'Directing, coordinating, monitoring'], correct: 0 },
      { text: 'What is active listening?', options: ['Fully concentrating', 'Partial hearing', 'Selective hearing', 'Ignoring speaker'], correct: 0 },
      { text: 'What is feedback?', options: ['Information about performance', 'Criticism only', 'Praise only', 'Command'], correct: 0 },
      { text: 'What is constructive feedback?', options: ['Helpful and specific', 'Negative only', 'Positive only', 'Vague comments'], correct: 0 },
      { text: 'What is delegation?', options: ['Assigning tasks', 'Doing all tasks', 'Avoiding tasks', 'Controlling tasks'], correct: 0 },
      { text: 'What are the benefits of delegation?', options: ['Empowers team', 'Increases workload', 'Creates confusion', 'Reduces quality'], correct: 0 },
      { text: 'What is conflict resolution?', options: ['Solving disagreements', 'Creating conflicts', 'Ignoring issues', 'Escalating problems'], correct: 0 },
      { text: 'What is mediation?', options: ['Third-party intervention', 'Direct negotiation', 'Arbitration', 'Litigation'], correct: 0 },
      { text: 'What is team building?', options: ['Improving collaboration', 'Individual work', 'Competition', 'Isolation'], correct: 0 },
      { text: 'What is motivation?', options: ['Drive to achieve', 'External pressure', 'Fear', 'Rewards only'], correct: 0 },
      { text: 'What is intrinsic motivation?', options: ['Internal satisfaction', 'External rewards', 'Fear of punishment', 'Peer pressure'], correct: 0 },
      { text: 'What is extrinsic motivation?', options: ['External rewards', 'Internal satisfaction', 'Personal growth', 'Self-fulfillment'], correct: 0 },
      { text: 'What is SMART goal?', options: ['Specific, Measurable, Achievable, Relevant, Time-bound', 'Simple, Measurable, Achievable, Relevant, Timely', 'Strategic, Measurable, Achievable, Relevant, Time-bound', 'Specific, Manageable, Achievable, Realistic, Time-bound'], correct: 0 },
      { text: 'What is strategic planning?', options: ['Long-term planning', 'Short-term planning', 'Daily planning', 'Hourly planning'], correct: 0 },
      { text: 'What is vision?', options: ['Future aspiration', 'Current state', 'Past achievement', 'Daily task'], correct: 0 },
      { text: 'What is mission?', options: ['Purpose of organization', 'Future goal', 'Past achievement', 'Individual task'], correct: 0 },
      { text: 'What are core values?', options: ['Guiding principles', 'Financial goals', 'Market targets', 'Product features'], correct: 0 },
      { text: 'What is organizational culture?', options: ['Shared values and beliefs', 'Physical infrastructure', 'Financial resources', 'Market position'], correct: 0 },
      { text: 'What is change management?', options: ['Managing organizational change', 'Resisting change', 'Ignoring change', 'Creating chaos'], correct: 0 },
      { text: 'What is Kotter\'s 8-step model?', options: ['Change management framework', 'Leadership style', 'Motivation theory', 'Communication model'], correct: 0 },
      { text: 'What is ADKAR model?', options: ['Individual change model', 'Organizational change model', 'Leadership model', 'Communication model'], correct: 0 },
    ];
    
    for (let i = 0; i < 30; i++) {
      const q = leadershipQuestions[i % leadershipQuestions.length];
      questions.push({
        id: i + 1,
        text: q.text,
        options: q.options,
        correct: q.correct
      });
    }
    return questions;
  };

  // Generate 30 HR Questions
  const generateHRQuestions = () => {
    const questions = [];
    const hrQuestions = [
      { text: 'What is Human Resource Management?', options: ['Managing employees', 'Managing finances', 'Managing operations', 'Managing marketing'], correct: 0 },
      { text: 'What is recruitment?', options: ['Hiring process', 'Training process', 'Firing process', 'Evaluation process'], correct: 0 },
      { text: 'What is onboarding?', options: ['New employee integration', 'Employee exit', 'Performance review', 'Salary negotiation'], correct: 0 },
      { text: 'What is offboarding?', options: ['Employee exit process', 'Employee joining', 'Training program', 'Performance review'], correct: 0 },
      { text: 'What is performance appraisal?', options: ['Employee evaluation', 'Salary payment', 'Leave approval', 'Recruitment'], correct: 0 },
      { text: 'What is 360-degree feedback?', options: ['Multi-source feedback', 'Single source feedback', 'Self feedback', 'Manager feedback'], correct: 0 },
      { text: 'What is KPI?', options: ['Key Performance Indicator', 'Key Process Indicator', 'Key Program Indicator', 'Key Product Indicator'], correct: 0 },
      { text: 'What is training needs analysis?', options: ['Identifying training needs', 'Conducting training', 'Evaluating training', 'Budgeting training'], correct: 0 },
      { text: 'What is succession planning?', options: ['Future leader development', 'Current hiring', 'Termination process', 'Salary planning'], correct: 0 },
      { text: 'What is compensation management?', options: ['Salary and benefits', 'Recruitment', 'Training', 'Performance review'], correct: 0 },
      { text: 'What is payroll?', options: ['Salary processing', 'Recruitment', 'Training', 'Performance review'], correct: 0 },
      { text: 'What is PF?', options: ['Provident Fund', 'Personal Fund', 'Performance Fund', 'Pension Fund'], correct: 0 },
      { text: 'What is ESI?', options: ['Employee State Insurance', 'Employee Stock Insurance', 'Employee Security Insurance', 'Employee Savings Insurance'], correct: 0 },
      { text: 'What is gratuity?', options: ['Long-term service benefit', 'Short-term bonus', 'Performance bonus', 'Attendance bonus'], correct: 0 },
      { text: 'What is leave policy?', options: ['Time off rules', 'Salary rules', 'Hiring rules', 'Termination rules'], correct: 0 },
      { text: 'What is maternity leave?', options: ['Leave for childbirth', 'Leave for illness', 'Leave for vacation', 'Leave for education'], correct: 0 },
      { text: 'What is paternity leave?', options: ['Leave for fatherhood', 'Leave for illness', 'Leave for vacation', 'Leave for education'], correct: 0 },
      { text: 'What is sick leave?', options: ['Leave for illness', 'Leave for vacation', 'Leave for education', 'Leave for family'], correct: 0 },
      { text: 'What is casual leave?', options: ['Short-term personal leave', 'Medical leave', 'Maternity leave', 'Study leave'], correct: 0 },
      { text: 'What is annual leave?', options: ['Yearly vacation leave', 'Sick leave', 'Maternity leave', 'Study leave'], correct: 0 },
      { text: 'What is attendance policy?', options: ['Work hours rules', 'Salary rules', 'Hiring rules', 'Termination rules'], correct: 0 },
      { text: 'What is code of conduct?', options: ['Behavioral guidelines', 'Salary guidelines', 'Hiring guidelines', 'Training guidelines'], correct: 0 },
      { text: 'What is POSH?', options: ['Prevention of Sexual Harassment', 'Policy of Safety at Work', 'Protection of Staff Health', 'Prevention of Safety Hazards'], correct: 0 },
      { text: 'What is grievance handling?', options: ['Addressing complaints', 'Creating complaints', 'Ignoring complaints', 'Escalating complaints'], correct: 0 },
      { text: 'What is employee engagement?', options: ['Commitment to work', 'Salary satisfaction', 'Job security', 'Work-life balance'], correct: 0 },
      { text: 'What is retention strategy?', options: ['Keeping employees', 'Hiring employees', 'Firing employees', 'Training employees'], correct: 0 },
      { text: 'What is attrition?', options: ['Employee turnover', 'Employee hiring', 'Employee training', 'Employee promotion'], correct: 0 },
      { text: 'What is HR analytics?', options: ['Data-driven HR decisions', 'Manual HR processes', 'Traditional HR', 'Basic HR'], correct: 0 },
      { text: 'What is HRIS?', options: ['HR Information System', 'HR Integration System', 'HR Implementation System', 'HR Internet System'], correct: 0 },
      { text: 'What is workforce planning?', options: ['Future staffing needs', 'Current staffing', 'Past staffing', 'Random staffing'], correct: 0 },
    ];
    
    for (let i = 0; i < 30; i++) {
      const q = hrQuestions[i % hrQuestions.length];
      questions.push({
        id: i + 1,
        text: q.text,
        options: q.options,
        correct: q.correct
      });
    }
    return questions;
  };

  // Generate 30 DSA Questions
  const generateDSAQuestions = () => {
    const questions = [];
    const dsaQuestions = [
  { text: 'What is a data structure?', options: ['A way to organize and store data', 'A programming language', 'A type of database', 'An algorithm'], correct: 0 },
  { text: 'What is an array?', options: ['Collection of elements at contiguous memory locations', 'Collection of linked nodes', 'Hierarchical data structure', 'Key-value pairs'], correct: 0 },
  { text: 'What is a linked list?', options: ['Sequence of nodes connected by pointers', 'Contiguous memory block', 'Tree structure', 'Hash table'], correct: 0 },
  { text: 'What is a stack?', options: ['LIFO (Last In First Out) data structure', 'FIFO (First In First Out) data structure', 'Hierarchical structure', 'Graph structure'], correct: 0 },
  { text: 'What is a queue?', options: ['FIFO (First In First Out) data structure', 'LIFO (Last In First Out) data structure', 'Tree structure', 'Graph structure'], correct: 0 },
  { text: 'What is time complexity?', options: ['Amount of time an algorithm takes to run', 'Amount of memory an algorithm uses', 'Number of lines in code', 'Speed of CPU'], correct: 0 },
  { text: 'What does Big O notation describe?', options: ['Upper bound of algorithm growth rate', 'Exact runtime', 'Lower bound only', 'Memory usage only'], correct: 0 },
  { text: 'What is O(1) time complexity?', options: ['Constant time - independent of input size', 'Linear time', 'Logarithmic time', 'Quadratic time'], correct: 0 },
  { text: 'What is O(n) time complexity?', options: ['Linear time - grows directly with input size', 'Constant time', 'Logarithmic time', 'Quadratic time'], correct: 0 },
  { text: 'What is a binary tree?', options: ['Tree where each node has at most 2 children', 'Tree with exactly 2 children per node', 'Tree with 3 children max', 'Tree with no children'], correct: 0 },
  { text: 'What is a Binary Search Tree (BST)?', options: ['Left child < parent < right child', 'Right child < parent < left child', 'All nodes equal', 'Random ordering'], correct: 0 },
  { text: 'What is the height of a tree?', options: ['Number of edges on longest path from root to leaf', 'Number of nodes in tree', 'Number of leaves', 'Depth of root'], correct: 0 },
  { text: 'What is a graph?', options: ['Set of vertices connected by edges', 'Tree structure only', 'Linear structure', 'Stack structure'], correct: 0 },
  { text: 'What is a hash table?', options: ['Data structure storing key-value pairs using hash function', 'Ordered list of elements', 'Tree structure', 'Graph structure'], correct: 0 },
  { text: 'What is a collision in hashing?', options: ['Two keys hash to same index', 'Hash function fails', 'Table becomes full', 'Key not found'], correct: 0 },
  { text: 'What is recursion?', options: ['Function that calls itself', 'Loop that repeats', 'Conditional statement', 'Variable declaration'], correct: 0 },
  { text: 'What is the base case in recursion?', options: ['Condition that stops recursion', 'First recursive call', 'Last recursive call', 'Return value'], correct: 0 },
  { text: 'What is bubble sort?', options: ['Repeatedly swapping adjacent elements if out of order', 'Dividing and conquering', 'Selecting minimum element', 'Inserting elements into sorted portion'], correct: 0 },
  { text: 'What is merge sort?', options: ['Divide array, sort halves, merge', 'Swap adjacent elements', 'Select minimum repeatedly', 'Insert elements into sorted portion'], correct: 0 },
  { text: 'What is quicksort?', options: ['Choose pivot, partition, recurse', 'Merge halves', 'Swap adjacent elements', 'Insert into sorted portion'], correct: 0 },
  { text: 'What is the worst-case time complexity of quicksort?', options: ['O(n²)', 'O(n log n)', 'O(n)', 'O(log n)'], correct: 0 },
  { text: 'What is binary search?', options: ['Repeatedly dividing sorted array in half', 'Searching each element one by one', 'Jumping by fixed steps', 'Using hash function'], correct: 0 },
  { text: 'What is a stack overflow?', options: ['Exceeding recursion call stack limit', 'Stack becomes empty', 'Queue becomes full', 'Memory leak'], correct: 0 },
  { text: 'What is dynamic programming?', options: ['Solving problems by breaking into overlapping subproblems', 'Divide and conquer only', 'Greedy approach', 'Randomized algorithm'], correct: 0 },
  { text: 'What is a greedy algorithm?', options: ['Makes locally optimal choice at each step', 'Explores all possibilities', 'Uses recursion only', 'Uses memoization'], correct: 0 },
  { text: 'What is a heap?', options: ['Complete binary tree with heap property', 'Balanced BST', 'Hash table', 'Graph'], correct: 0 },
  { text: 'What is a priority queue?', options: ['Elements accessed by priority, not FIFO', 'FIFO queue', 'LIFO stack', 'Double-ended queue'], correct: 0 },
  { text: 'What is Dijkstra\'s algorithm used for?', options: ['Shortest path in weighted graph', 'Sorting array', 'Searching tree', 'Hashing keys'], correct: 0 },
  { text: 'What is an AVL tree?', options: ['Self-balancing BST with balance factor -1,0,1', 'Binary heap', 'Regular BST', 'Graph'], correct: 0 },
  { text: 'What is the time complexity of searching in a balanced BST?', options: ['O(log n)', 'O(n)', 'O(n log n)', 'O(1)'], correct: 0 }
];
    
    for (let i = 0; i < 30; i++) {
      const q = dsaQuestions[i % dsaQuestions.length];
      questions.push({
        id: i + 1,
        text: q.text,
        options: q.options,
        correct: q.correct
      });
    }
    return questions;
  };

  // Generate 30 PMP Questions
  const generatePMPQuestions = () => {
    const questions = [];
    const pmpQuestions = [
      { text: 'What is project management?', options: ['Applying knowledge and skills', 'Writing code', 'Managing people', 'Budgeting'], correct: 0 },
      { text: 'What is a project?', options: ['Temporary endeavor', 'Ongoing operation', 'Daily task', 'Routine work'], correct: 0 },
      { text: 'What is a program?', options: ['Group of projects', 'Single project', 'Portfolio', 'Task'], correct: 0 },
      { text: 'What is a portfolio?', options: ['Group of programs', 'Single project', 'Single program', 'Task'], correct: 0 },
      { text: 'What is project charter?', options: ['Project authorization document', 'Budget document', 'Schedule document', 'Risk document'], correct: 0 },
      { text: 'What is scope statement?', options: ['Project boundaries', 'Budget details', 'Schedule details', 'Risk details'], correct: 0 },
      { text: 'What is WBS?', options: ['Work Breakdown Structure', 'Work Budget Structure', 'Work Schedule Structure', 'Work Risk Structure'], correct: 0 },
      { text: 'What is critical path?', options: ['Longest path', 'Shortest path', 'Easiest path', 'Hardest path'], correct: 0 },
      { text: 'What is Gantt chart?', options: ['Project schedule view', 'Budget view', 'Risk view', 'Quality view'], correct: 0 },
      { text: 'What is PERT?', options: ['Program Evaluation Review Technique', 'Project Evaluation Review Technique', 'Program Estimation Review Technique', 'Project Estimation Review Technique'], correct: 0 },
      { text: 'What is risk management?', options: ['Identifying and responding to risks', 'Ignoring risks', 'Creating risks', 'Avoiding all risks'], correct: 0 },
      { text: 'What is risk register?', options: ['Risk documentation', 'Budget document', 'Schedule document', 'Quality document'], correct: 0 },
      { text: 'What is stakeholder management?', options: ['Managing stakeholders', 'Ignoring stakeholders', 'Avoiding stakeholders', 'Removing stakeholders'], correct: 0 },
      { text: 'What is communication management?', options: ['Planning communications', 'Ignoring communications', 'Limiting communications', 'Removing communications'], correct: 0 },
      { text: 'What is quality management?', options: ['Ensuring quality standards', 'Ignoring quality', 'Reducing quality', 'Removing quality'], correct: 0 },
      { text: 'What is cost management?', options: ['Budget planning and control', 'Increasing costs', 'Reducing costs', 'Ignoring costs'], correct: 0 },
      { text: 'What is earned value management?', options: ['Performance measurement', 'Cost measurement', 'Schedule measurement', 'Quality measurement'], correct: 0 },
      { text: 'What is CPI?', options: ['Cost Performance Index', 'Cost Process Index', 'Cost Program Index', 'Cost Project Index'], correct: 0 },
      { text: 'What is SPI?', options: ['Schedule Performance Index', 'Schedule Process Index', 'Schedule Program Index', 'Schedule Project Index'], correct: 0 },
      { text: 'What is ROI?', options: ['Return on Investment', 'Risk on Investment', 'Return on Interest', 'Risk on Interest'], correct: 0 },
      { text: 'What is a milestone?', options: ['Significant event', 'Small task', 'Budget item', 'Risk factor'], correct: 0 },
      { text: 'What is a deliverable?', options: ['Tangible output', 'Meeting', 'Report', 'Budget'], correct: 0 },
      { text: 'What is change management?', options: ['Controlling changes', 'Making changes', 'Avoiding changes', 'Removing changes'], correct: 0 },
      { text: 'What is change control board?', options: ['Approves changes', 'Makes changes', 'Avoids changes', 'Removes changes'], correct: 0 },
      { text: 'What is project closure?', options: ['Finalizing project', 'Starting project', 'Planning project', 'Executing project'], correct: 0 },
      { text: 'What is lessons learned?', options: ['Project documentation', 'Budget documentation', 'Schedule documentation', 'Risk documentation'], correct: 0 },
      { text: 'What is agile project management?', options: ['Iterative approach', 'Waterfall approach', 'V-model approach', 'Spiral approach'], correct: 0 },
      { text: 'What is a sprint?', options: ['Time-boxed iteration', 'Meeting', 'Review', 'Planning'], correct: 0 },
      { text: 'What is scrum master?', options: ['Facilitates scrum process', 'Develops code', 'Tests software', 'Manages budget'], correct: 0 },
      { text: 'What is product owner?', options: ['Manages backlog', 'Develops code', 'Tests software', 'Manages budget'], correct: 0 },
    ];
    
    for (let i = 0; i < 30; i++) {
      const q = pmpQuestions[i % pmpQuestions.length];
      questions.push({
        id: i + 1,
        text: q.text,
        options: q.options,
        correct: q.correct
      });
    }
    return questions;
  };

  const getAvailableTests = () => {
    const userDesignation = currentUser?.role || 'employee';
    return tests.filter(test => test.designation.includes(userDesignation));
  };

  const startTest = (test) => {
    setSelectedTest(test);
    setCurrentQuestion(0);
    setAnswers({});
    setTestSubmitted(false);
    setScore(0);
    setResults(null);
    setTimeRemaining(test.duration * 60);
    setTimerActive(true);
  };

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers({ ...answers, [questionId]: answerIndex });
  };

  const handleNext = () => {
    if (currentQuestion < selectedTest.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = () => {
    setTimerActive(false);
    let correctCount = 0;
    const resultsData = selectedTest.questions.map(question => {
      const isCorrect = answers[question.id] === question.correct;
      if (isCorrect) correctCount++;
      return {
        question: question.text,
        selected: answers[question.id],
        correct: question.correct,
        isCorrect,
        correctAnswer: question.options[question.correct]
      };
    });
    
    const percentage = (correctCount / selectedTest.questions.length) * 100;
    setScore(percentage);
    setResults(resultsData);
    setTestSubmitted(true);
    
    const testResult = {
      id: Date.now(),
      testId: selectedTest.id,
      testTitle: selectedTest.title,
      userId: currentUser?.id,
      userName: currentUser?.name,
      score: percentage,
      date: new Date().toISOString(),
      answers: resultsData
    };
    
    const savedResults = localStorage.getItem('testResults');
    const allResults = savedResults ? JSON.parse(savedResults) : [];
    allResults.push(testResult);
    localStorage.setItem('testResults', JSON.stringify(allResults));
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadge = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-700';
    if (score >= 60) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const availableTests = getAvailableTests();
  const showHRView = ['hr', 'admin', 'superadmin'].includes(userRole);

  // Test List View
  const TestListView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {availableTests.map(test => (
        <div key={test.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-blue-100 rounded-lg p-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <span className="text-xs text-gray-500">{test.duration} minutes</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{test.title}</h3>
            <p className="text-sm text-gray-500 mb-4">{test.totalQuestions} Questions</p>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {test.designation.map(role => (
                <span key={role} className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                  {role}
                </span>
              ))}
            </div>
            <button
              onClick={() => startTest(test)}
              className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Start Test
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // Test Taking View
  const TestTakingView = () => {
    const question = selectedTest.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / selectedTest.questions.length) * 100;
    const answeredCount = Object.keys(answers).length;
    
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold text-gray-800">{selectedTest.title}</h2>
              <div className="text-right">
                <span className="text-sm text-gray-500">Time Left: </span>
                <span className={`font-mono font-bold ${timeRemaining < 300 ? 'text-red-600' : 'text-gray-700'}`}>
                  {formatTime(timeRemaining)}
                </span>
              </div>
            </div>
            <div className="flex justify-between text-sm text-gray-500 mb-2">
              <span>Question {currentQuestion + 1} of {selectedTest.questions.length}</span>
              <span>Answered: {answeredCount}/{selectedTest.questions.length}</span>
            </div>
            <div className="mt-2">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-800 mb-6">{question.text}</h3>
            <div className="space-y-3">
              {question.options.map((option, idx) => (
                <label key={idx} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition">
                  <input
                    type="radio"
                    name="question"
                    value={idx}
                    checked={answers[question.id] === idx}
                    onChange={() => handleAnswer(question.id, idx)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
            >
              Previous
            </button>
            <div className="flex gap-2">
              {currentQuestion < selectedTest.questions.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  Submit Test
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Results View
  const ResultsView = () => (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 text-center bg-gradient-to-r from-green-50 to-blue-50">
          <svg className="w-16 h-16 text-green-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-gray-800">Test Completed!</h2>
          <p className="text-gray-600 mt-1">{selectedTest.title}</p>
        </div>
        
        <div className="p-6 text-center">
          <div className="mb-6">
            <div className={`text-5xl font-bold ${getScoreColor(score)}`}>{Math.round(score)}%</div>
            <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm ${getScoreBadge(score)}`}>
              {score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : 'Need Improvement'}
            </span>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-2xl font-bold text-gray-800">{selectedTest.questions.length}</p>
              <p className="text-xs text-gray-500">Total Questions</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-2xl font-bold text-green-600">{results.filter(r => r.isCorrect).length}</p>
              <p className="text-xs text-gray-500">Correct</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-2xl font-bold text-red-600">{results.filter(r => !r.isCorrect).length}</p>
              <p className="text-xs text-gray-500">Incorrect</p>
            </div>
          </div>
          
          <button
            onClick={() => setSelectedTest(null)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Back to Tests
          </button>
        </div>
      </div>
    </div>
  );

  // Results History for HR View
  const ResultsHistory = () => {
    const [allResults, setAllResults] = useState([]);
    const [filter, setFilter] = useState('all');
    
    useEffect(() => {
      const savedResults = localStorage.getItem('testResults');
      if (savedResults) {
        setAllResults(JSON.parse(savedResults));
      }
    }, []);
    
    const filteredResults = filter === 'all' ? allResults : allResults.filter(r => r.score >= 80);
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Employee Test Results</h2>
            <p className="text-sm text-gray-500">Track performance across all tests</p>
          </div>
          <select
            className="px-3 py-1 border rounded-lg text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">All Results</option>
            <option value="passed">Passed Only (≥80%)</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Test</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredResults.length > 0 ? (
                filteredResults.map(result => (
                  <tr key={result.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{result.userName || 'Employee'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{result.testTitle}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`text-sm font-semibold ${getScoreColor(result.score)}`}>
                        {Math.round(result.score)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">
                      {new Date(result.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${getScoreBadge(result.score)}`}>
                        {result.score >= 80 ? 'Passed' : result.score >= 60 ? 'Average' : 'Failed'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No test results available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Performance Management</h1>
        <p className="text-sm text-gray-500 mt-1">Take skill assessments and track your performance</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('tests')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'tests' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Available Tests
        </button>
        {showHRView && (
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'history' 
                ? 'text-blue-600 border-b-2 border-blue-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Results History
          </button>
        )}
      </div>

      {/* Content */}
      {selectedTest ? (
        testSubmitted ? <ResultsView /> : <TestTakingView />
      ) : (
        activeTab === 'tests' ? <TestListView /> : <ResultsHistory />
      )}
    </div>
  );
};

export default Performance;